// src/utils/debugReport.js
//
// Lightweight client-side debug log capture + export.
// Purpose: support/debug in desktop builds where DevTools may be unavailable.
//
// Privacy: we redact common secret formats before storing/exporting.

import { isTauriApp, getTauriInvoke } from './tauriHelper';

const STORAGE_KEY = 'thryveloop_debug_log_v1';
const MAX_ENTRIES = 250;

const safeNowIso = () => {
  try {
    return new Date().toISOString();
  } catch (_) {
    return 'unknown-time';
  }
};

const redactSecrets = (input) => {
  const str = (input == null) ? '' : String(input);
  return str
    // OpenAI-style keys
    .replace(/sk-[A-Za-z0-9]{10,}/g, 'sk-REDACTED')
    // Bearer tokens
    .replace(/Bearer\\s+[A-Za-z0-9._\\-]{10,}/g, 'Bearer REDACTED')
    // JWT-ish tokens
    .replace(/eyJ[A-Za-z0-9_\\-]+\\.[A-Za-z0-9_\\-]+\\.[A-Za-z0-9_\\-]+/g, 'JWT_REDACTED')
    // LemonSqueezy-ish license keys (best-effort)
    .replace(/[A-Z0-9]{4,}-[A-Z0-9]{4,}-[A-Z0-9]{4,}-[A-Z0-9]{4,}/g, 'LICENSE_REDACTED');
};

const safeStringify = (value) => {
  const seen = new WeakSet();
  try {
    return JSON.stringify(value, (key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]';
        seen.add(val);
      }
      if (val instanceof Error) {
        return { name: val.name, message: val.message, stack: val.stack };
      }
      return val;
    });
  } catch (_) {
    try {
      return String(value);
    } catch {
      return '[Unserializable]';
    }
  }
};

const argToLogString = (arg) => {
  try {
    if (arg instanceof Error) {
      return arg.stack || `${arg.name}: ${arg.message}`;
    }
    if (typeof arg === 'string') return arg;
    return safeStringify(arg);
  } catch (_) {
    return '[Unserializable]';
  }
};

const readStored = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const writeStored = (entries) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch (_) {
    // ignore
  }
};

export const recordDebugEvent = (level, message, meta = null) => {
  if (typeof window === 'undefined') return;
  const entry = {
    at: safeNowIso(),
    level: redactSecrets(level || 'info'),
    message: redactSecrets(message || ''),
    meta: meta ? redactSecrets(safeStringify(meta).slice(0, 4000)) : null,
  };

  const current = readStored();
  current.push(entry);
  writeStored(current);
};

export const initDebugLogCapture = () => {
  if (typeof window === 'undefined') return;
  if (window.__THRYVELOOP_DEBUG_CAPTURED__) return;
  window.__THRYVELOOP_DEBUG_CAPTURED__ = true;

  // Patch console.warn/error to persist logs for later export.
  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);

  console.warn = (...args) => {
    try {
      recordDebugEvent('warn', args.map(argToLogString).join(' '));
    } catch (_) {
      // ignore
    }
    originalWarn(...args);
  };

  console.error = (...args) => {
    try {
      recordDebugEvent('error', args.map(argToLogString).join(' '));
    } catch (_) {
      // ignore
    }
    originalError(...args);
  };

  window.addEventListener('error', (event) => {
    try {
      recordDebugEvent('window.error', event?.message || 'Uncaught error', {
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
        stack: event?.error?.stack,
      });
    } catch (_) {
      // ignore
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      recordDebugEvent('window.unhandledrejection', event?.reason?.message || 'Unhandled rejection', {
        stack: event?.reason?.stack,
      });
    } catch (_) {
      // ignore
    }
  });
};

export const getDebugLogEntries = () => readStored();

export const buildDebugReport = async ({
  aiSettings = null,
  lastErrorDetails = null,
} = {}) => {
  const isTauri = isTauriApp();
  const lines = [];
  lines.push(`# ThryveLoop Debug Report`);
  lines.push(`Generated: ${safeNowIso()}`);
  lines.push(`Runtime: ${isTauri ? 'tauri-desktop' : 'web'}`);

  try {
    if (typeof window !== 'undefined') {
      lines.push(`UserAgent: ${navigator.userAgent}`);
      lines.push(`URL: ${window.location.href}`);
    }
  } catch (_) {
    // ignore
  }

  if (isTauri) {
    try {
      const invoke = await getTauriInvoke({ forceProbe: true });
      if (typeof invoke !== 'function') {
        throw new Error('Tauri bridge unavailable');
      }
      const sys = await invoke('get_system_info');
      if (sys) {
        lines.push(`AppVersion: ${sys.app_version}`);
        lines.push(`OS: ${sys.os} (${sys.arch})`);
      }
    } catch (error) {
      lines.push(`SystemInfo: unavailable (${redactSecrets(error?.message || String(error))})`);
    }
  }

  if (aiSettings) {
    lines.push('');
    lines.push('## LLM Settings (redacted)');
    lines.push(`Provider: ${redactSecrets(aiSettings.llmProvider || '')}`);
    lines.push(`Endpoint: ${redactSecrets(aiSettings.llmApiUrl || '')}`);
    lines.push(`Model: ${redactSecrets(aiSettings.llmModel || '')}`);
    lines.push(`Enabled: ${Boolean(aiSettings.llmEnabled)}`);
  }

  if (lastErrorDetails) {
    lines.push('');
    lines.push('## Last Error Details');
    lines.push(redactSecrets(lastErrorDetails));
  }

  const entries = getDebugLogEntries();
  if (entries.length) {
    lines.push('');
    lines.push(`## Recent Logs (${Math.min(entries.length, MAX_ENTRIES)})`);
    entries.slice(-MAX_ENTRIES).forEach((e) => {
      lines.push(`[${e.at}] ${e.level}: ${e.message}${e.meta ? ` | ${e.meta}` : ''}`);
    });
  }

  return lines.join('\n');
};

export const copyTextToClipboard = async (text) => {
  const value = (text || '').toString();
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  // Fallback for restricted clipboard environments.
  try {
    const el = document.createElement('textarea');
    el.value = value;
    el.setAttribute('readonly', 'true');
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch (_) {
    return false;
  }
};
